import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, ChevronDown, ChevronUp, Save, X } from 'lucide-react';

// Componente Modal
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button
            className="button secondary"
            onClick={onClose}
            style={{ padding: '8px' }}
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Função auxiliar para determinar a cor da barra de progresso
const getProgressColor = (progress) => {
  if (progress >= 100) return '#28a745'; // Verde
  if (progress >= 75) return '#20c997'; // Verde claro
  if (progress >= 50) return '#17a2b8'; // Azul
  if (progress >= 25) return '#ffc107'; // Amarelo
  return '#dc3545'; // Vermelho
};

const GerenciadorItens = ({ itens, setItens, readOnly = false }) => {
  const [itemAtivo, setItemAtivo] = useState(null);
  const [modalItemAberto, setModalItemAberto] = useState(false);
  const [modalDetalheAberto, setModalDetalheAberto] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [detalheEditando, setDetalheEditando] = useState(null);

  // Form states
  const [formItem, setFormItem] = useState({
    item: '',
    descricao: '',
    unidade: '',
    preco: '',
    quantidade: '',
  });

  const [formDetalhe, setFormDetalhe] = useState({
    data: '',
    operacao: 'Medição',
    numeroMedicao: '',
    descricaoMedicao: '',
    quantidade: '',
    valor: '',
  });

  // Resetar formulário de item
  const resetFormItem = () => {
    setFormItem({
      item: '',
      descricao: '',
      unidade: '',
      preco: '',
      quantidade: '',
    });
    setItemEditando(null);
  };

  // Resetar formulário de detalhe
  const resetFormDetalhe = () => {
    try {
      // Determinar o próximo número de medição
      let proximoNumero = 1;
      
      if (itemAtivo && itemAtivo.detalhes && Array.isArray(itemAtivo.detalhes) && itemAtivo.detalhes.length > 0) {
        // Coletar todos os números de medição válidos
        const numerosMedicao = [];
        
        itemAtivo.detalhes.forEach(detalhe => {
          if (detalhe && detalhe.operacao === 'Medição') {
            let num = null;
            
            // Tentar obter o número da medição de várias formas possíveis
            if (detalhe.numeroMedicao !== undefined && detalhe.numeroMedicao !== null) {
              num = parseInt(detalhe.numeroMedicao);
            } else if (detalhe.numero_medicao !== undefined && detalhe.numero_medicao !== null) {
              num = parseInt(detalhe.numero_medicao);
            } else if (detalhe.descricaoMedicao) {
              // Tentar extrair o número da descrição (ex: "Medição 3")
              const match = detalhe.descricaoMedicao.match(/\\d+/);
              if (match) {
                num = parseInt(match[0]);
              }
            }
            
            if (!isNaN(num) && num > 0) {
              numerosMedicao.push(num);
            }
          }
        });
        
        // Ordenar os números para garantir que encontramos o maior
        numerosMedicao.sort((a, b) => a - b);
        
        // O próximo número é o maior encontrado + 1, ou 1 se nenhum foi encontrado
        proximoNumero = numerosMedicao.length > 0 ? 
          numerosMedicao[numerosMedicao.length - 1] + 1 : 1;
          
        console.log('Próximo número de medição para o item:', proximoNumero, 'Números encontrados:', numerosMedicao);
      }

      setFormDetalhe({
        data: new Date().toISOString().split('T')[0],
        operacao: 'Medição',
        numeroMedicao: proximoNumero,
        descricaoMedicao: `Medição ${proximoNumero}`,
        quantidade: '',
        valor: '',
      });
      setDetalheEditando(null);
    } catch (error) {
      console.error('Erro ao resetar formulário de detalhe:', error);
      // Em caso de erro, usar valores padrão
      setFormDetalhe({
        data: new Date().toISOString().split('T')[0],
        operacao: 'Medição',
        numeroMedicao: 1,
        descricaoMedicao: 'Medição 1',
        quantidade: '',
        valor: '',
      });
      setDetalheEditando(null);
    }
  };

  // Inicializar formulário de detalhe com a data atual
  useEffect(() => {
    resetFormDetalhe();
  }, []);

  // Calcular o total do item
  const calcularTotalItem = (preco, quantidade) => {
    return parseFloat(preco || 0) * parseFloat(quantidade || 0);
  };

  // Calcular a quantidade medida (soma das quantidades dos detalhes)
  const calcularQuantidadeMedida = (detalhes) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return 0;
    
    // Garantir que estamos somando apenas números válidos
    return detalhes.reduce((total, detalhe) => {
      const quantidade = parseFloat(detalhe.quantidade);
      return total + (isNaN(quantidade) ? 0 : quantidade);
    }, 0);
  };

  // Calcular o total medido (soma dos totais dos detalhes)
  const calcularTotalMedido = (detalhes) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return 0;
    
    // Garantir que estamos somando apenas números válidos
    return detalhes.reduce((total, detalhe) => {
      const valorTotal = parseFloat(detalhe.total);
      return total + (isNaN(valorTotal) ? 0 : valorTotal);
    }, 0);
  };

  // Calcular o progresso (quantidade medida / quantidade total)
  const calcularProgresso = (quantidadeMedida, quantidade) => {
    const qtdMedida = parseFloat(quantidadeMedida);
    const qtdTotal = parseFloat(quantidade);
    
    if (isNaN(qtdTotal) || qtdTotal === 0) return 0;
    if (isNaN(qtdMedida)) return 0;
    
    const progresso = (qtdMedida / qtdTotal) * 100;
    return Math.min(100, Math.max(0, progresso)); // Limitar entre 0 e 100
  };

  // Adicionar ou atualizar item
  const salvarItem = () => {
    if (!formItem.item || !formItem.descricao || !formItem.unidade || !formItem.preco || !formItem.quantidade) {
      alert('Todos os campos são obrigatórios');
      return;
    }

    const total = calcularTotalItem(formItem.preco, formItem.quantidade);

    if (itemEditando) {
      // Atualizar item existente
      const novosItens = itens.map(item =>
        item.id === itemEditando.id
          ? {
            ...item,
            ...formItem,
            preco: parseFloat(formItem.preco),
            quantidade: parseFloat(formItem.quantidade),
            total
          }
          : item
      );
      setItens(novosItens);
    } else {
      // Adicionar novo item
      const novoItem = {
        id: Date.now(),
        ...formItem,
        preco: parseFloat(formItem.preco),
        quantidade: parseFloat(formItem.quantidade),
        total,
        detalhes: [],
        quantidadeMedida: 0,
        totalMedido: 0,
        progresso: 0
      };
      setItens([...itens, novoItem]);
    }

    setModalItemAberto(false);
    resetFormItem();
  };

  // Adicionar ou atualizar detalhe
  const salvarDetalhe = () => {
    if (!formDetalhe.data || !formDetalhe.operacao || !formDetalhe.quantidade) {
      alert('Data, operação e quantidade são obrigatórios');
      return;
    }

    if (!itemAtivo) return;

    // Garantir que os valores são números válidos
    const valor = parseFloat(formDetalhe.valor || itemAtivo.preco) || 0;
    const quantidade = parseFloat(formDetalhe.quantidade) || 0;
    const total = valor * quantidade;

    const novoDetalhe = {
      id: detalheEditando ? detalheEditando.id : Date.now(),
      itemId: itemAtivo.id, // Adicionar o ID do item ao detalhe para facilitar a identificação
      ...formDetalhe,
      numeroMedicao: parseInt(formDetalhe.numeroMedicao) || null,
      quantidade: quantidade,
      valor: valor,
      total: total
    };

    // Atualizar detalhes do item ativo
    const novosItens = itens.map(item => {
      if (item.id !== itemAtivo.id) return item;

      let novosDetalhes;
      if (detalheEditando) {
        // Atualizar detalhe existente
        novosDetalhes = item.detalhes.map(d =>
          d.id === detalheEditando.id ? novoDetalhe : d
        );
      } else {
        // Adicionar novo detalhe
        novosDetalhes = [...item.detalhes, novoDetalhe];
      }

      // Calcular saldos diretamente
      // Ordenar detalhes por data
      novosDetalhes.sort((a, b) => new Date(a.data) - new Date(b.data));
      
      // Inicializar com o valor total do item
      let saldoQuantidade = parseFloat(item.quantidade) || 0;
      let saldoValor = parseFloat(item.total) || 0;
      
      console.log('Calculando saldos diretamente para item:', item.item);
      console.log('Quantidade Total:', saldoQuantidade);
      console.log('Valor Total:', saldoValor);
      
      // Calcular saldos para cada detalhe
      novosDetalhes = novosDetalhes.map((detalhe, index) => {
        // Garantir que os valores são números
        const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
        const valorDetalhe = parseFloat(detalhe.valor) || 0;
        const totalDetalhe = qtdDetalhe * valorDetalhe;
        
        console.log(`Detalhe ${index + 1}:`, {
          data: detalhe.data,
          quantidade: qtdDetalhe,
          valor: valorDetalhe,
          total: totalDetalhe,
          saldoAntes: { qtd: saldoQuantidade, valor: saldoValor }
        });
        
        // Deduzir do saldo a quantidade e valor da medição atual
        saldoQuantidade -= qtdDetalhe;
        saldoValor -= totalDetalhe;
        
        // Garantir que os saldos não sejam negativos
        const saldoQtdFinal = Math.max(0, saldoQuantidade);
        const saldoValorFinal = Math.max(0, saldoValor);
        
        console.log(`Saldo após detalhe ${index + 1}:`, { 
          qtd: saldoQtdFinal, 
          valor: saldoValorFinal 
        });
        
        return {
          ...detalhe,
          quantidade: qtdDetalhe,
          valor: valorDetalhe,
          total: totalDetalhe,
          saldoQuantidade: saldoQtdFinal,
          saldoValor: saldoValorFinal
        };
      });

      // Recalcular métricas do item
      const quantidadeMedida = calcularQuantidadeMedida(novosDetalhes);
      const totalMedido = calcularTotalMedido(novosDetalhes);
      const progresso = calcularProgresso(quantidadeMedida, item.quantidade);

      return {
        ...item,
        detalhes: novosDetalhes,
        quantidadeMedida,
        totalMedido,
        progresso
      };
    });

    setItens(novosItens);
    setModalDetalheAberto(false);
    resetFormDetalhe();
  };

  // Calcular saldos para cada detalhe
  const calcularSaldos = (detalhes) => {
    if (!detalhes || !Array.isArray(detalhes) || detalhes.length === 0) return [];

    try {
      // Encontrar o item atual ao qual os detalhes pertencem
      let itemAtual = null;
      
      // Primeiro, tentar usar o itemAtivo (item que está sendo editado atualmente)
      if (itemAtivo) {
        itemAtual = itemAtivo;
        console.log('Usando itemAtivo para calcular saldos:', itemAtivo.item);
      } 
      // Se não houver itemAtivo, procurar nos itens pelo ID do primeiro detalhe
      else if (detalhes[0] && detalhes[0].id) {
        for (const item of itens) {
          if (item.detalhes && Array.isArray(item.detalhes)) {
            const encontrado = item.detalhes.some(d => d && d.id === detalhes[0].id);
            if (encontrado) {
              itemAtual = item;
              console.log('Item encontrado pelo ID do detalhe:', item.item);
              break;
            }
          }
        }
      }
      
      // Se ainda não encontrou, usar o primeiro item da lista (último recurso)
      if (!itemAtual && itens.length > 0) {
        // Tentar encontrar o item pelo ID do detalhe em todos os itens
        for (const item of itens) {
          if (item.id === detalhes[0]?.itemId) {
            itemAtual = item;
            console.log('Item encontrado pelo itemId do detalhe:', item.item);
            break;
          }
        }
      }
      
      // Se ainda não encontrou, usar o primeiro item da lista (último recurso)
      if (!itemAtual && itens.length > 0) {
        itemAtual = itens[0];
        console.log('Usando primeiro item como fallback:', itens[0].item);
      }

      if (!itemAtual) {
        console.error('Não foi possível encontrar o item para os detalhes:', detalhes[0]);
        return detalhes.map(d => ({
          ...d,
          saldoQuantidade: 0,
          saldoValor: 0
        }));
      }

      // Ordenar detalhes por data
      const detalhesSorted = [...detalhes].sort((a, b) => {
        try {
          const dataA = new Date(a.data);
          const dataB = new Date(b.data);
          return dataA - dataB;
        } catch (error) {
          console.error('Erro ao ordenar por data:', error);
          return 0;
        }
      });

      // Inicializar com o valor total do item
      const qtdTotal = parseFloat(itemAtual.quantidade) || 0;
      const valorTotal = parseFloat(itemAtual.total) || 0;
      const precoUnitario = parseFloat(itemAtual.preco) || 0;
      
      console.log('Calculando saldos para item:', itemAtual.item);
      console.log('Quantidade Total:', qtdTotal);
      console.log('Valor Total:', valorTotal);
      console.log('Preço Unitário:', precoUnitario);
      console.log('Número de detalhes:', detalhesSorted.length);
      
      // Inicializar saldos com o total do item
      let saldoQtdRestante = qtdTotal;
      let saldoValorRestante = valorTotal;

      // Mapear os detalhes com os saldos calculados
      return detalhesSorted.map((detalhe, index) => {
        try {
          // Garantir que os valores são números
          const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
          const valorDetalhe = parseFloat(detalhe.valor || precoUnitario) || 0;
          const totalDetalhe = qtdDetalhe * valorDetalhe;
          
          console.log(`Detalhe ${index + 1}:`, {
            data: detalhe.data,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoAntes: { qtd: saldoQtdRestante, valor: saldoValorRestante }
          });
          
          // Deduzir do saldo a quantidade e valor da medição atual
          saldoQtdRestante -= qtdDetalhe;
          saldoValorRestante -= totalDetalhe;
          
          // Garantir que os saldos não sejam negativos
          const saldoQtdFinal = Math.max(0, saldoQtdRestante);
          const saldoValorFinal = Math.max(0, saldoValorRestante);
          
          console.log(`Saldo após detalhe ${index + 1}:`, { 
            qtd: saldoQtdFinal, 
            valor: saldoValorFinal 
          });
          
          return {
            ...detalhe,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoQuantidade: saldoQtdFinal,
            saldoValor: saldoValorFinal
          };
        } catch (error) {
          console.error('Erro ao processar detalhe:', error, detalhe);
          return {
            ...detalhe,
            quantidade: parseFloat(detalhe.quantidade) || 0,
            valor: parseFloat(detalhe.valor) || 0,
            total: parseFloat(detalhe.total) || 0,
            saldoQuantidade: 0,
            saldoValor: 0
          };
        }
      });
    } catch (error) {
      console.error('Erro ao calcular saldos:', error);
      return detalhes.map(d => ({
        ...d,
        saldoQuantidade: 0,
        saldoValor: 0
      }));
    }
  };

  // Editar item
  const editarItem = (item) => {
    setItemEditando(item);
    setFormItem({
      item: item.item,
      descricao: item.descricao,
      unidade: item.unidade,
      preco: item.preco.toString(),
      quantidade: item.quantidade.toString(),
    });
    setModalItemAberto(true);
  };

  // Excluir item
  const excluirItem = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      setItens(itens.filter(item => item.id !== id));
      if (itemAtivo && itemAtivo.id === id) {
        setItemAtivo(null);
      }
    }
  };

  // Editar detalhe
  const editarDetalhe = (detalhe) => {
    try {
      console.log('Editando detalhe:', detalhe);
      
      // Garantir que temos valores válidos
      const quantidade = parseFloat(detalhe.quantidade) || 0;
      const valor = parseFloat(detalhe.valor) || 0;
      
      // Determinar o número da medição
      let numeroMedicao = '';
      if (detalhe.numeroMedicao !== undefined && detalhe.numeroMedicao !== null) {
        numeroMedicao = detalhe.numeroMedicao.toString();
      } else if (detalhe.numero_medicao !== undefined && detalhe.numero_medicao !== null) {
        numeroMedicao = detalhe.numero_medicao.toString();
      }
      
      // Determinar a descrição da medição
      let descricaoMedicao = '';
      if (detalhe.descricaoMedicao) {
        descricaoMedicao = detalhe.descricaoMedicao;
      } else if (detalhe.descricao_medicao) {
        descricaoMedicao = detalhe.descricao_medicao;
      } else if (numeroMedicao) {
        descricaoMedicao = `Medição ${numeroMedicao}`;
      } else {
        descricaoMedicao = detalhe.operacao || 'Medição';
      }
      
      setDetalheEditando(detalhe);
      setFormDetalhe({
        data: detalhe.data,
        operacao: detalhe.operacao || 'Medição',
        numeroMedicao: numeroMedicao,
        descricaoMedicao: descricaoMedicao,
        quantidade: quantidade.toString(),
        valor: valor.toString(),
      });
      setModalDetalheAberto(true);
    } catch (error) {
      console.error('Erro ao editar detalhe:', error);
      alert('Erro ao editar detalhe. Verifique o console para mais informações.');
    }
  };

  // Excluir detalhe
  const excluirDetalhe = (detalheId) => {
    if (!itemAtivo) return;
    if (window.confirm('Tem certeza que deseja excluir este detalhamento?')) {
      const novosItens = itens.map(item => {
        if (item.id !== itemAtivo.id) return item;

        const novosDetalhes = item.detalhes.filter(d => d.id !== detalheId);

        // Recalcular saldos diretamente
        // Ordenar detalhes por data
        novosDetalhes.sort((a, b) => new Date(a.data) - new Date(b.data));
        
        // Inicializar com o valor total do item
        let saldoQuantidade = parseFloat(item.quantidade) || 0;
        let saldoValor = parseFloat(item.total) || 0;
        
        console.log('Recalculando saldos após exclusão para item:', item.item);
        console.log('Quantidade Total:', saldoQuantidade);
        console.log('Valor Total:', saldoValor);
        
        // Calcular saldos para cada detalhe
        const detalhesComSaldos = novosDetalhes.map((detalhe, index) => {
          // Garantir que os valores são números
          const qtdDetalhe = parseFloat(detalhe.quantidade) || 0;
          const valorDetalhe = parseFloat(detalhe.valor) || 0;
          const totalDetalhe = qtdDetalhe * valorDetalhe;
          
          console.log(`Detalhe ${index + 1}:`, {
            data: detalhe.data,
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoAntes: { qtd: saldoQuantidade, valor: saldoValor }
          });
          
          // Deduzir do saldo a quantidade e valor da medição atual
          saldoQuantidade -= qtdDetalhe;
          saldoValor -= totalDetalhe;
          
          // Garantir que os saldos não sejam negativos
          const saldoQtdFinal = Math.max(0, saldoQuantidade);
          const saldoValorFinal = Math.max(0, saldoValor);
          
          console.log(`Saldo após detalhe ${index + 1}:`, { 
            qtd: saldoQtdFinal, 
            valor: saldoValorFinal 
          });
          
          return {
            ...detalhe,
            itemId: item.id, // Garantir que o itemId está presente
            quantidade: qtdDetalhe,
            valor: valorDetalhe,
            total: totalDetalhe,
            saldoQuantidade: saldoQtdFinal,
            saldoValor: saldoValorFinal
          };
        });

        // Recalcular métricas do item
        const quantidadeMedida = calcularQuantidadeMedida(detalhesComSaldos);
        const totalMedido = calcularTotalMedido(detalhesComSaldos);
        const progresso = calcularProgresso(quantidadeMedida, item.quantidade);

        return {
          ...item,
          detalhes: detalhesComSaldos,
          quantidadeMedida,
          totalMedido,
          progresso
        };
      });

      setItens(novosItens);
    }
  };

  // Formatar valor para exibição
  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Formatar percentual para exibição
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  return (
    <div className="gerenciador-itens" style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>Itens da Obra</h3>

        {!readOnly && (
          <button
            type="button"
            className="button"
            onClick={() => {
              resetFormItem();
              setModalItemAberto(true);
            }}
          >
            <Plus size={16} />
            Adicionar Item
          </button>
        )}
      </div>

      {/* Lista de Itens */}
      {itens.length === 0 ? (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #dee2e6'
        }}>
          <p style={{ margin: 0, color: '#666' }}>
            Nenhum item adicionado. Clique em "Adicionar Item" para começar.
          </p>
        </div>
      ) : (
        <div>
          {/* Cabeçalho da tabela */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '0.4fr 2fr 0.4fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.5fr',
            gap: '8px',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px 8px 0 0',
            fontWeight: 'bold',
            fontSize: '14px',
            width: '100%',
            overflowX: 'auto'
          }}>
            <div>Item</div>
            <div>Descrição</div>
            <div>Un.</div>
            <div>Preço</div>
            <div>Qtd.</div>
            <div>Total</div>
            <div>Qtd. Medida</div>
            <div>Total Medido</div>
            <div>Progresso</div>
            <div></div>
          </div>

          {/* Itens */}
          {itens.map(item => (
            <div key={item.id} style={{ marginBottom: '5px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '0.4fr 2fr 0.4fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.5fr',
                gap: '8px',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: itemAtivo?.id === item.id ? '0' : '8px',
                border: '1px solid #dee2e6',
                alignItems: 'center',
                fontSize: '14px',
                width: '100%'
              }}>
                <div>{item.item}</div>
                <div>{item.descricao}</div>
                <div>{item.unidade}</div>
                <div>{formatarValor(item.preco)}</div>
                <div>{item.quantidade}</div>
                <div>{formatarValor(item.total)}</div>
                <div>{item.quantidadeMedida || 0}</div>
                <div>{formatarValor(item.totalMedido || 0)}</div>
                <div>
                  <div style={{
                    width: '100%',
                    height: '20px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${item.progresso || 0}%`,
                      height: '100%',
                      backgroundColor: getProgressColor(item.progresso || 0),
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {formatarPercentual(item.progresso || 0)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    type="button"
                    className="button secondary"
                    style={{ padding: '4px', fontSize: '12px' }}
                    onClick={() => setItemAtivo(itemAtivo?.id === item.id ? null : item)}
                  >
                    {itemAtivo?.id === item.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {!readOnly && (
                    <>
                      <button
                        type="button"
                        className="button secondary"
                        style={{ padding: '4px', fontSize: '12px' }}
                        onClick={() => editarItem(item)}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        type="button"
                        className="button danger"
                        style={{ padding: '4px', fontSize: '12px' }}
                        onClick={() => excluirItem(item.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Detalhes do item */}
              {itemAtivo?.id === item.id && (
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '0 0 8px 8px',
                  border: '1px solid #dee2e6',
                  borderTop: 'none'
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ margin: 0 }}>Detalhamento do Item</h5>
                  </div>

                  {!readOnly && (
                    <div style={{ marginBottom: '15px' }}>
                      <button
                        type="button"
                        className="button"
                        onClick={() => {
                          resetFormDetalhe();
                          setModalDetalheAberto(true);
                        }}
                      >
                        <Plus size={14} />
                        Adicionar Detalhamento
                      </button>
                    </div>
                  )}

                  {/* Lista de Detalhes */}
                  {item.detalhes && item.detalhes.length > 0 ? (
                    <div>
                      {/* Cabeçalho da tabela de detalhes */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '0.8fr 1fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.5fr',
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '8px 8px 0 0',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        width: '100%',
                        overflowX: 'auto'
                      }}>
                        <div>Data</div>
                        <div>Medição</div>
                        <div>Quantidade</div>
                        <div>Valor Unit.</div>
                        <div>Total</div>
                        <div>Saldo Qtd.</div>
                        <div>Saldo Valor</div>
                        <div></div>
                      </div>

                      {/* Agrupar detalhes por medição */}
                      {(() => {
                        // Agrupar por número de medição ou data se não houver número
                        const detalhesAgrupados = {};
                        item.detalhes.forEach(detalhe => {
                          const chave = detalhe.numeroMedicao
                            ? `Medição ${detalhe.numeroMedicao}`
                            : `${detalhe.operacao} - ${new Date(detalhe.data).toLocaleDateString('pt-BR')}`;

                          if (!detalhesAgrupados[chave]) {
                            detalhesAgrupados[chave] = [];
                          }
                          detalhesAgrupados[chave].push(detalhe);
                        });

                        // Renderizar detalhes agrupados
                        return Object.entries(detalhesAgrupados).map(([grupo, detalhes]) => (
                          <React.Fragment key={grupo}>
                            {/* Cabeçalho do grupo */}
                            <div style={{
                              gridColumn: '1 / -1',
                              backgroundColor: '#e2e3e5',
                              padding: '6px 10px',
                              fontWeight: 'bold',
                              fontSize: '13px',
                              borderBottom: '1px solid #dee2e6'
                            }}>
                              {grupo}
                            </div>

                            {/* Detalhes do grupo */}
                            {detalhes.map(detalhe => (
                              <div key={detalhe.id} style={{
                                display: 'grid',
                                gridTemplateColumns: '0.8fr 1fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 0.5fr',
                                gap: '8px',
                                padding: '8px',
                                backgroundColor: 'white',
                                borderBottom: '1px solid #dee2e6',
                                fontSize: '13px',
                                alignItems: 'center',
                                width: '100%'
                              }}>
                                <div>{new Date(detalhe.data).toLocaleDateString('pt-BR')}</div>
                                <div>{detalhe.descricao_medicao || detalhe.operacao}</div>
                                <div>{detalhe.quantidade}</div>
                                <div>{formatarValor(detalhe.valor)}</div>
                                <div>{formatarValor(detalhe.total)}</div>
                                <div>{detalhe.saldo_quantidade !== undefined ? parseFloat(detalhe.saldo_quantidade).toFixed(2) : '0.00'}</div>
                                <div>{detalhe.saldo_valor !== undefined ? formatarValor(parseFloat(detalhe.saldo_valor)) : 'R$ 0,00'}</div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  {!readOnly && (
                                    <>
                                      <button
                                        type="button"
                                        className="button secondary"
                                        style={{ padding: '3px', fontSize: '11px' }}
                                        onClick={() => editarDetalhe(detalhe)}
                                      >
                                        <Edit size={12} />
                                      </button>
                                      <button
                                        type="button"
                                        className="button danger"
                                        style={{ padding: '3px', fontSize: '11px' }}
                                        onClick={() => excluirDetalhe(detalhe.id)}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </React.Fragment>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div style={{
                      padding: '15px',
                      textAlign: 'center',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px dashed #dee2e6'
                    }}>
                      <p style={{ margin: 0, color: '#666' }}>
                        Nenhum detalhamento adicionado para este item.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal para Editar/Adicionar Item */}
      <Modal
        isOpen={modalItemAberto}
        onClose={() => setModalItemAberto(false)}
        title={itemEditando ? 'Editar Item' : 'Novo Item'}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>Item</label>
            <input
              type="text"
              value={formItem.item}
              onChange={(e) => setFormItem({ ...formItem, item: e.target.value })}
              placeholder="Ex: 1.1"
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <input
              type="text"
              value={formItem.descricao}
              onChange={(e) => setFormItem({ ...formItem, descricao: e.target.value })}
              placeholder="Ex: Escavação manual"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>Unidade</label>
            <input
              type="text"
              value={formItem.unidade}
              onChange={(e) => setFormItem({ ...formItem, unidade: e.target.value })}
              placeholder="Ex: m³"
            />
          </div>
          <div className="form-group">
            <label>Preço Unitário (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formItem.preco}
              onChange={(e) => setFormItem({ ...formItem, preco: e.target.value })}
              placeholder="Ex: 150.00"
            />
          </div>
          <div className="form-group">
            <label>Quantidade</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formItem.quantidade}
              onChange={(e) => setFormItem({ ...formItem, quantidade: e.target.value })}
              placeholder="Ex: 10.5"
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="button secondary" onClick={() => setModalItemAberto(false)}>
            Cancelar
          </button>
          <button className="button" onClick={salvarItem}>
            <Save size={16} />
            {itemEditando ? 'Atualizar Item' : 'Adicionar Item'}
          </button>
        </div>
      </Modal>

      {/* Modal para Editar/Adicionar Detalhe */}
      <Modal
        isOpen={modalDetalheAberto}
        onClose={() => setModalDetalheAberto(false)}
        title={detalheEditando ? 'Editar Detalhamento' : 'Novo Detalhamento'}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>Data</label>
            <input
              type="date"
              value={formDetalhe.data}
              onChange={(e) => setFormDetalhe({ ...formDetalhe, data: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Operação</label>
            <select
              value={formDetalhe.operacao}
              onChange={(e) => setFormDetalhe({ ...formDetalhe, operacao: e.target.value })}
            >
              <option value="Medição">Medição</option>
              <option value="Ajuste">Ajuste</option>
              <option value="Correção">Correção</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>Número da Medição</label>
            <input
              type="number"
              min="1"
              value={formDetalhe.numeroMedicao}
              onChange={(e) => {
                const num = parseInt(e.target.value) || '';
                setFormDetalhe({ 
                  ...formDetalhe, 
                  numeroMedicao: num,
                  descricaoMedicao: num ? `Medição ${num}` : formDetalhe.descricaoMedicao
                });
              }}
              placeholder="Ex: 1"
            />
          </div>
          <div className="form-group">
            <label>Descrição da Medição</label>
            <input
              type="text"
              value={formDetalhe.descricaoMedicao}
              onChange={(e) => setFormDetalhe({ ...formDetalhe, descricaoMedicao: e.target.value })}
              placeholder="Ex: Medição 1"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>Quantidade</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formDetalhe.quantidade}
              onChange={(e) => setFormDetalhe({ ...formDetalhe, quantidade: e.target.value })}
              placeholder="Ex: 5.5"
            />
          </div>
          <div className="form-group">
            <label>Valor Unitário (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formDetalhe.valor}
              onChange={(e) => setFormDetalhe({ ...formDetalhe, valor: e.target.value })}
              placeholder={`Padrão: ${itemAtivo ? itemAtivo.preco : 0}`}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Se não informado, será usado o preço do item: {itemAtivo ? formatarValor(itemAtivo.preco) : 'N/A'}
            </small>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="button secondary" onClick={() => setModalDetalheAberto(false)}>
            Cancelar
          </button>
          <button className="button" onClick={salvarDetalhe}>
            <Save size={16} />
            {detalheEditando ? 'Atualizar Detalhamento' : 'Adicionar Detalhamento'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GerenciadorItens;